import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import remove from 'lodash/remove';
import { createSelector } from 'reselect';

import { EVasesPromoCodes, IAdditionalProps, IVasInfo } from '@r1-frontend/api-domru/api-profile/v1/vas/dto/ResponseDto';

import { TState } from '~/src/store';

export const selectAllVas = createSelector(
    (state: TState) => state.vas,
    vasInfo => {
        return [...vasInfo.default, ...vasInfo.antivirus].sort((a, b) => b.includedAgr - a.includedAgr);
    },
);

export const multiBundleId = 1468;

interface IPriceOptionItem {
    count: number,
    price: number,
    discount?: number,
}

export interface IMultibundleAdditional extends IAdditionalProps {
    priceOptions: IPriceOptionItem[],
    subscriptionsSocIds: {
        active: number[],
        inactive: number[],
    },
}

export interface ISubscriptionAdditional extends IAdditionalProps {
    multiBundle?: {
        socId: number,
        exclusive: 0 | 1,
        phone: null | string,
        active: 0 | 1,
    },
}

type TSubscriptions = IMultibundleAdditional & ISubscriptionAdditional;

export interface IFullMultibundle extends IVasInfo<IMultibundleAdditional> {
    subscriptions: IVasInfo<ISubscriptionAdditional>[],
    widgetOrder?: EVasOrder,
}

export const selectMultibundleAndSubscriptions = createSelector(
    (state: TState) => state.vas?.subscriptions as unknown as IVasInfo<TSubscriptions>[],
    (vasSubscriptions) => {
        const groupedSubscriptions = groupBy(vasSubscriptions, ({ vasId }) => vasId === multiBundleId);
        const subscriptions: IVasInfo<ISubscriptionAdditional>[] = groupedSubscriptions.false;

        if (groupedSubscriptions.true?.length) {
            const multibundle: IVasInfo<IMultibundleAdditional> = groupedSubscriptions.true[0];
            const subscriptionsInMultibundle = remove(
                subscriptions,
                sub => sub.additionalProps.multiBundle && multibundle.additionalProps.subscriptionsSocIds.active.includes(sub.additionalProps.multiBundle.socId),
            );
            const fullMultibundle: IFullMultibundle = { ...multibundle, subscriptions: subscriptionsInMultibundle };

            return {
                multibundle: fullMultibundle,
                subscriptions,
            };
        }

        return {
            multibundle: null,
            subscriptions,
        };
    },
);

export const selectInactiveSubscriptions = createSelector(
    [
        (state: TState) => state.vas.isLoadingSubscriptions,
        selectMultibundleAndSubscriptions,
    ],
    (isLoadingSubscriptions, { multibundle, subscriptions }) => ({
        multibundle,
        subscriptions: subscriptions?.filter(({ activeVas }) => !activeVas),
        isLoadingSubscriptions,
    }),
);

enum EVasTypes {
    contents = 'contents',
    ipAddress = 'ip_address',
    speedBonus = 'speed_bonus',
    tvPacks = 'tv_packs',
    multibundle = 'multibundle',
}

export enum EVasOrder {
    default = -1,
    multibundle = 0,
    tvPackets = 1,
    standart = 2,
    promoCode = 3,
    speedBonus = 4,
    staticIp = 5,
    watchControl = 6,
    watchEverywhere = 7,
    antivirus = 8,
}

const contentTypeIds = {
    antivirus: [1, 3, 9],
    subscription: 2,
    watchControl: 4,
    watchEverywhere: 12,
};

export interface IVasWithOrder<T = IAdditionalProps> extends IVasInfo<T> {
    widgetOrder: EVasOrder,
    subscriptions?: IVasInfo<ISubscriptionAdditional>[],
}

export const selectVasByOrder = createSelector(
    selectAllVas,
    allVases => {
        const vases = [...allVases];

        if (vases.length) {
            const multibundle = remove(vases, vas => vas.type === EVasTypes.multibundle)[0] as unknown as IVasInfo<IMultibundleAdditional>;
            const multibundleFull: IVasWithOrder<IMultibundleAdditional> | undefined = multibundle
                ? { ...multibundle, widgetOrder: EVasOrder.multibundle, subscriptions: [] }
                : undefined;

            const vasWithOrder = vases.reduce((acc: IVasWithOrder[], currentVas) => {
                const vas: IVasWithOrder = { ...currentVas, widgetOrder: EVasOrder.default };

                if (vas.type === EVasTypes.contents) {
                    if (vas.contentTypeId === contentTypeIds.subscription) {
                        const subscription = { ...vas } as unknown as IVasWithOrder<ISubscriptionAdditional>;
                        const { multiBundle } = subscription.additionalProps;

                        if (
                            multibundleFull &&
                            multiBundle &&
                            multibundleFull.additionalProps.subscriptionsSocIds.active.includes(multiBundle?.socId)
                        ) {
                            multibundleFull.subscriptions?.push(subscription);
                        } else {
                            subscription.widgetOrder = EVasOrder.standart;
                            acc.push(subscription);
                        }
                    } else {
                        switch (true) {
                            case (vas.contentTypeId === EVasesPromoCodes.gamer || vas.contentTypeId === EVasesPromoCodes.voucher || vas.promocode):
                                vas.widgetOrder = EVasOrder.promoCode;
                                break;
                            case (contentTypeIds.antivirus.includes(vas.contentTypeId)):
                                vas.widgetOrder = EVasOrder.antivirus;
                                break;
                            case (vas.contentTypeId === contentTypeIds.watchControl):
                                vas.widgetOrder = EVasOrder.watchControl;
                                break;
                            case (vas.contentTypeId === contentTypeIds.watchEverywhere):
                                vas.widgetOrder = EVasOrder.watchEverywhere;
                                break;
                        }
                        acc.push(vas);
                    }
                } else {
                    switch (true) {
                        case (vas.type === EVasTypes.speedBonus):
                            vas.widgetOrder = EVasOrder.speedBonus;
                            break;
                        case (vas.type === EVasTypes.ipAddress):
                            vas.widgetOrder = EVasOrder.staticIp;
                            break;
                        case (vas.type === EVasTypes.tvPacks && vas.contentTypeId === 0):
                            vas.widgetOrder = EVasOrder.tvPackets;
                            break;
                    }
                    acc.push(vas);
                }

                return acc;
            }, []);

            const orderedVasList = orderBy(vasWithOrder, ['includedAgr', 'widgetOrder'], ['desc', 'asc']);
            return multibundleFull ? [multibundleFull, ...orderedVasList] : orderedVasList;
        }

        return [];
    },
);

const WARFACE_SOC_ID = 4692;

export const selectActiveVas = createSelector(
    selectVasByOrder,
    /**
     * ad-hoc
     * временно фильтруем активные подписки, исключая Warface, так как он
     * приходит на новых рельсах api-widget
     *
     * Причины:
     *
     * 1. Пока не можем вывести подписки GamerBase из-за проблем в категориях контента (задача на ресерч WEB-44141)
     * 2. Если скроем игровые подписки по contentTypeId в запросе v1/vas, то скроется и Warface, а он необходим
     */
    (vases) => vases.filter(({ activeVas, socId }) => activeVas && socId !== WARFACE_SOC_ID),
);

export const WATCH_CONTROL_SERVICE_ID = 1978;

export const selectIsWatchControlAvailable = createSelector(
    selectVasByOrder,
    (vases) => vases.some(
        ({ serviceId, activeVas }) => serviceId === WATCH_CONTROL_SERVICE_ID && !activeVas,
    ),
);

/**
 * Returns only those services whose retrieval is not yet implemented in api-widgets
 *
 * Filters services based on three parameters:
 *
 * contentTypeId, one of:
 * 4 - watchControl
 * 6 - voucher
 * 12 - watchEverywhere
 * 13 - gamer
 *
 * type = ip_address or multibundle
 *
 * promocode = 1
 */
export const selectActiveVasForWidgets = createSelector(
    selectActiveVas,
    vases => vases.filter(
        ({ type, contentTypeId, promocode }) => ['ip_address', 'multibundle'].includes(type) ||
        [4, 6, 12, 13].includes(contentTypeId) ||
        promocode === 1,
    ),
);
